import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  CreditTransactionResponseDataSchema,
  CreditTransactionsResponseDataSchema,
  CreditTransactionTypeResponse,
} from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof CreditTransactionResponseDataSchema>
>()

type Props = {
  creditTransactions: z.infer<typeof CreditTransactionsResponseDataSchema>
}

export function UsageTable({ creditTransactions }: Props) {
  const { t } = useTranslation()

  const types = useMemo(
    () => ({
      [CreditTransactionTypeResponse.Purchase]: t("subscription.usage.table.types.purchase"),
      [CreditTransactionTypeResponse.Consumption]: t("subscription.usage.table.types.consumption"),
      [CreditTransactionTypeResponse.Adjustment]: t("subscription.usage.table.types.adjustment"),
      [CreditTransactionTypeResponse.Expired]: t("subscription.usage.table.types.expired"),
    }),
    [t],
  )

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("type", {
          header: t("subscription.usage.table.type"),
          cell: ({ row }) => {
            return types[row.original.type]
          },
        }),
        columnHelper.accessor("amount", {
          header: t("subscription.usage.table.amount"),
          cell: ({ row }) => {
            const isPositive = row.original.amount >= 0
            const value = Math.abs(row.original.amount)
            return `${isPositive ? "+" : "-"}${value}`
          },
        }),
        columnHelper.accessor("createdAt", {
          header: t("subscription.usage.table.createdAt"),
          cell: ({ row }) => {
            return <DateDisplay date={row.original.createdAt} />
          },
          meta: {
            align: "right",
          },
        }),
      ]),
    [types, t],
  )

  const table = useTable({
    data: creditTransactions,
    columns,
    features,
  })

  function renderContent() {
    if (creditTransactions.length === 0) {
      return (
        <TableEmptySlate colSpan={columns.length} message={t("subscription.usage.table.empty")} />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
